import { Input } from '@/shared/ui';
import { FormikErrors, FormikTouched } from 'formik';
import { AddressValidation, ExtendedAddress } from '@/shared/components/address-form/types';
import { withScrollRestoration } from '@/shared/hocs/withScrollRestoration';
import { acceptNumeric } from '@/shared/helpers/input';
import { ChangeEvent, useCallback } from 'react';
import AdditionalAddressInputsNeededAlert from '../address-form/components/additional-address-inputs-needed';

const FormLabel = ({ label, boldLabel, required, error }: { label: string; boldLabel: boolean; required?: boolean; error?: boolean }) => (
    <div className="flex justify-start items-center gap-1">
        <span className={`font-dmSans text-16 leading-[125%] ${boldLabel ? 'font-dmSansBold font-bold' : 'font-normal'} ${error ? 'text-red-500' : ''}`}>
            {label}
        </span>
        {required && <span className="font-bold text-red-500">*</span>}
    </div>
);

interface ManualAddressProps {
    handleChange: (fieldname: string, value: string) => void;
    values: ExtendedAddress;
    errors: FormikErrors<ExtendedAddress>;
    touched: FormikTouched<ExtendedAddress>;
    selectedAddressState?: AddressValidation;
    error?: string;
    showApartmentSuiteBoxNumberField?: boolean;
    editedAddress?: boolean;
    moreThanOneFieldTouchedAndInsistShowSingleError?: boolean;
    useFormLabel?: boolean;
}

export const ManualAddressFormElements: React.FC<ManualAddressProps> = ({
    handleChange,
    values,
    errors,
    touched,
    selectedAddressState,
    error = '',
    showApartmentSuiteBoxNumberField,
    editedAddress,
    moreThanOneFieldTouchedAndInsistShowSingleError = false,
    useFormLabel = false,
}) => {
    const handleNumericChange = useCallback((field: string, handleChange: (field: string, value: string) => void) => {
        return (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            acceptNumeric(() => {
                handleChange(field, event.target.value);
            })(event);
        };
    }, []);

    const makeLabel = (text: string) => useFormLabel
        ? (error: boolean, boldLabel: boolean, required?: boolean) => <FormLabel label={text} boldLabel={boldLabel} required={required} error={error} />
        : text;

    return (
        <>
            {error && <span data-testid="manual-address-form-elements-error" className="mb-4 block">{selectedAddressState?.error}</span>}
            <Input
                className="manual_address__form_elements__address"
                label={makeLabel('Street Address')}
                name="addressLine1"
                showErrorMessage={!moreThanOneFieldTouchedAndInsistShowSingleError}
                type="text"
                placeholder="ex. 28520 Wood Canyon Dr."
                value={values.addressLine1}
                error={touched.addressLine1 && Boolean(errors.addressLine1)}
                errorMessage={errors.addressLine1}
                onChange={event => handleChange('addressLine1', event.target.value)}
                required
                testId="manual-address-address-line-1"
                alertClassName='!mt-[.625rem]'
                labelToReflectError
            />
            {(showApartmentSuiteBoxNumberField || values.unit || editedAddress) && (
                <Input
                    className="manual_address__form_elements__unit"
                    label={makeLabel('Apartment/Suite/Box Number')}
                    name="unit"
                    showErrorMessage={!moreThanOneFieldTouchedAndInsistShowSingleError}
                    type="text"
                    placeholder="ex. #123, Suite 303"
                    value={values.unit}
                    error={touched?.unit && Boolean(errors?.unit)}
                    errorMessage={errors?.unit}
                    onChange={event => handleChange('unit', event.target.value)}
                    testId="manual-address-unit"
                    alertClassName='!mt-[.625rem]'
                    labelToReflectError
                />
            )}
            {selectedAddressState?.needsUnit && <AdditionalAddressInputsNeededAlert />}

            <Input
                className="manual_address__form_elements__city"
                label={makeLabel('City')}
                name="city"
                showErrorMessage={!moreThanOneFieldTouchedAndInsistShowSingleError}
                type="text"
                placeholder="ex. Aliso Viejo"
                error={touched.city && Boolean(errors.city)}
                errorMessage={errors.city}
                value={values.city}
                onChange={event => handleChange('city', event.target.value)}
                required
                testId="manual-address-city"
                alertClassName='!mt-[.625rem]'
                labelToReflectError
            />
            <div className="flex flex-wrap gap-4">
                <Input
                    label={makeLabel('State')}
                    name="state"
                    type="text"
                    showErrorMessage={!moreThanOneFieldTouchedAndInsistShowSingleError}
                    placeholder="ex. CA"
                    error={touched.state && Boolean(errors.state)}
                    errorMessage={errors.state}
                    value={values.state}
                    onChange={event => handleChange('state', event.target.value)}
                    maxLength={2}
                    required
                    className="manual_address__form_elements__state min-w-[10rem] flex-1"
                    testId="manual-address-state"
                    alertClassName='!mt-[.625rem]'
                    labelToReflectError
                />
                <Input
                    label={makeLabel('Zip')}
                    name="zip"
                    type="text"
                    showErrorMessage={!moreThanOneFieldTouchedAndInsistShowSingleError}
                    placeholder="ex. 92656"
                    error={touched.zip && Boolean(errors.zip)}
                    errorMessage={errors.zip}
                    value={values.zip}
                    onChange={handleNumericChange('zip', handleChange)}
                    required
                    maxLength={5}
                    className="manual_address__form_elements__zip min-w-[10rem] flex-1"
                    testId="manual-address-zip"
                    alertClassName='!mt-[.625rem]'
                    labelToReflectError
                />
            </div>
        </>
    );
};

export default withScrollRestoration(ManualAddressFormElements);
